/**
 * `Catálogo_de_Eventos` — single source of telemetry event names and attributes
 * for the Snap Reports frontend.
 *
 * Feature `telemetry-observability`, task 5.1 (Requirement 11.1; design
 * component 8, "Data Models"). This module is the **type contract only**: it
 * declares the discriminated union of every telemetry event and its supporting
 * categorical types. It intentionally contains **no** emit logic, Faro wiring,
 * sanitization or hooks — those land in tasks 5.2 (`telemetry.helper.ts`) and
 * 5.3 (`useTelemetry.ts`).
 *
 * Design invariants encoded here:
 * - Discriminated union keyed on `name`; referencing an event or attribute not
 *   declared here fails TypeScript compilation (Req 11.1). No `any`, and no
 *   loose/literal event strings live outside this catalog.
 * - Every attribute is **exclusively** categorical, numeric or a route pattern.
 *   No user input, no PII, no report content, no concrete resource ids ever
 *   enter an event payload (Req 12.10, 14) — the cardinality budget in the
 *   design (≤ 100 series/metric, ≤ 50 values/label) depends on these closed
 *   sets.
 * - Enumerated label domains are exported as `as const` tuples so downstream
 *   consumers (the sanitizer allowlist, the `loki.process` `stage.metrics`
 *   label budget) share one source of truth instead of re-declaring strings.
 */

import type { ReportMode } from "~/types/global";
import { REDES_SOCIAIS_FIELD_LABELS, REPORT_SECTIONS, TRANSLATED_LABELS } from "~/helpers/constants";

/**
 * Report creation mode. Re-exported from the canonical definition in
 * `~/types/global` (`"individual" | "relacoes" | "multiplos"`, mirrored by the
 * `reportMode` slice of `newReportStore`) so the catalog stays the single
 * import site for telemetry types without forking the definition.
 */
export type { ReportMode };

/**
 * Closed set of report section identifiers, taken verbatim from
 * `ui_group_titles.json` (49 keys, e.g. "processos", "telefones") — the same
 * word-map that supplies every section's render-time `title` (`REPORT_SECTIONS.imagens`
 * is compared against `section.title` elsewhere in the codebase). Since a
 * section's title is always one of these 49 values, this is a genuine closed
 * set, not free text: it doubles as the reverse-lookup table
 * `resolveSectionKey()` (telemetry.helper.ts) uses to turn a runtime title
 * back into a stable key. `"unknown"` is the fallback for a title that
 * doesn't match any of them (defensive only — should not happen in practice).
 */
export type SectionKey = keyof typeof REPORT_SECTIONS | "unknown";

/**
 * Closed set of field identifiers, taken from `translate_prop_to_label.json`'s
 * property keys (e.g. "cpf", "full name") — the same dictionary that supplies
 * a field's display label via `translatePropToLabel()`. `resolveFieldKey()`
 * (telemetry.helper.ts) reverse-looks-up a rendered label back to this key.
 * `"unknown"` covers a label with no entry in the dictionary — an
 * untranslated backend field name, a whole-entry block header (e.g.
 * "TELEFONE 1"), or no label at all — so a raw label can never leak through
 * as free text.
 */
// redes_sociais's own field-label vocabulary (per-network SectionModels,
// standalone frontend copy of the processor's authoritative catalog — see
// REDES_SOCIAIS_FIELD_LABELS) is a mostly disjoint set from the ~30 generic
// sections' TRANSLATED_LABELS, so FieldKey unions both closed sets rather
// than trying to force one dictionary to cover both provenances.
export type FieldKey =
  | keyof typeof TRANSLATED_LABELS
  | keyof typeof REDES_SOCIAIS_FIELD_LABELS
  | "unknown";

/**
 * Closed set of PERSISTED report types. NOT derived from
 * `REPORT_CONSTANTS.types` (`report_types.json`) — that list is missing
 * "lote" (confirmed against `REPORT_LIST_TYPE_OPTIONS`/
 * `USER_LOGS_REPORT_TYPE_OPTIONS` in `helpers/constants.ts`, both of which
 * carry it), so a batch report's `reportType` would wrongly resolve to
 * `"unknown"`. Distinct from {@link ReportMode}, which is the creation-flow
 * mode (individual/relações/lotes) chosen before a report exists; this is the
 * type of a report already open, read from `ReportTypeContext`
 * (`useReportType()`). `resolveReportType()` (telemetry.helper.ts) maps a
 * runtime value to this set, or `"unknown"` when it doesn't match — same
 * defensive pattern as `SectionKey`/`FieldKey`.
 */
export const REPORT_TYPE_KEYS = [
  "cpf",
  "cnpj",
  "email",
  "telefone",
  "redes_sociais",
  "relacoes",
  "combinado",
  "lote",
] as const;

export type ReportTypeKey = (typeof REPORT_TYPE_KEYS)[number] | "unknown";

/**
 * Stable funnel step names, derived from the create-report dialog button
 * `testId`s in `useCreateReportDialogButtons` (the `button-` prefix is
 * stripped). Events use these names via a step→name map and never emit a raw
 * value such as `step:2` (Req 12.2).
 */
export const FUNNEL_STABLE_NAMES = [
  "proceed-to-review",
  "back-to-input",
  "confirm-create-report",
  "confirm-create-multiple-reports",
  "confirm-create-folder",
  "cancel-create-report",
] as const;

/** A funnel step identified by its stable, testId-derived name. */
export type FunnelStableName = (typeof FUNNEL_STABLE_NAMES)[number];

/**
 * Closed set of route patterns, taken verbatim from the `<Route path>` values
 * declared in `components/router/Router.tsx`. Navigation and error telemetry
 * emit the **pattern**, never the concrete URL with ids (Req 12.7, 13.3).
 *
 * `report/:type/:id` is the nested route path exactly as declared (no leading
 * slash). `unknown` is the closed-set fallback for any path not matched above,
 * so a concrete URL can never leak into telemetry.
 */
export const ROUTE_PATTERNS = [
  "/login",
  "/dashboard",
  "/",
  "/pasta/:folderId",
  "report/:type/:id",
  "/conta/configuracoes",
  "/conta/suporte",
  "/sem-permissao",
  "unknown",
] as const;

/** A route referenced by its stable pattern, never a concrete URL. */
export type RoutePattern = (typeof ROUTE_PATTERNS)[number];

/**
 * Non-sensitive failure categories attached to funnel/export/summary error
 * events, enabling error-rate-by-mode analysis without leaking error detail
 * (Req 12.12, 13.3). Deliberately coarse and categorical.
 */
export const FAILURE_CATEGORIES = [
  "network",
  "timeout",
  "validation",
  "client",
  "server",
  "unknown",
] as const;

/** A coarse, non-sensitive classification of a failure. */
export type FailureCategory = (typeof FAILURE_CATEGORIES)[number];

/** Lifecycle phase of an export / AI-summary operation. */
export type TelemetryPhase = "start" | "success" | "error";

/**
 * `Catálogo_de_Eventos` — the discriminated union of every telemetry event the
 * frontend may emit. The `name` field is the discriminant.
 *
 * Attribute rules (enforced by the shapes below): only categorical values
 * (`ReportMode`, `FunnelStableName`, `RoutePattern`, `FailureCategory`,
 * `TelemetryPhase`), numeric counters/durations, and the `create_to_ready`
 * flow tag. `batchSize` is `1` outside batch mode (Req 12.1). No attribute
 * carries user input, PII, report content or concrete resource ids.
 */
export type TelemetryEvent =
  | { name: "report_funnel_open"; mode: ReportMode; batchSize: number }
  // Primeiro caractere digitado em QUALQUER campo do modo ativo (fluxo 1.1
  // complementar) — dispara uma vez por diálogo, não por campo, pra ficar
  // comparável entre os 3 modos (relações tem 2 campos; individual e lotes
  // têm 1). Ver useCreateReportDialogButtons.tsx.
  | { name: "report_funnel_input_started"; mode: ReportMode }
  | { name: "report_funnel_advance"; step: FunnelStableName; mode: ReportMode; batchSize: number }
  | { name: "report_funnel_back"; step: FunnelStableName; mode: ReportMode }
  | { name: "report_funnel_cancel"; step: FunnelStableName; mode: ReportMode }
  | { name: "report_funnel_submit"; mode: ReportMode; batchSize: number }
  | { name: "report_funnel_success"; mode: ReportMode; batchSize: number }
  | { name: "report_funnel_error"; mode: ReportMode; failureCategory: FailureCategory }
  | { name: "report_funnel_abandon"; step: FunnelStableName; mode: ReportMode }
  | { name: "batch_review_friction"; invalidRate: number; removedCount: number; durationMs: number }
  | { name: "navigation"; routePattern: RoutePattern }
  | { name: "export_pdf" | "export_docx" | "ai_summary"; phase: TelemetryPhase; durationMs?: number }
  | { name: "perceived_latency"; flow: "create_to_ready"; durationMs: number }
  | { name: "ui_theme_changed"; theme: "light" | "dark" }
  // Emitted once per app shell mount (Header.tsx, useTheme's init effect) —
  // a snapshot of the theme in effect right now, not a change. Answers "which
  // theme predominates" (fluxo 6.2), which ui_theme_changed's change-only
  // count structurally can't: an account that never toggles never emits one.
  | { name: "ui_theme_observed"; theme: "light" | "dark" }
  // reportType comes from ReportTypeContext (useReportType()) — the report
  // currently open, not the creation-flow mode. Resolved via
  // resolveReportType() so an unrecognized/legacy value never leaks as free
  // text.
  | { name: "report_section_toggled"; action: "open" | "close"; section: SectionKey; reportType: ReportTypeKey }
  // One event per distinct section with data in the opened report (soft-deleted
  // included, empty excluded), once per report open after processing finishes.
  // Denominator of the per-section access rate; see getSectionsPresentInReport()
  // and collectPresentSectionKeys().
  | { name: "report_section_present"; section: SectionKey; reportType: ReportTypeKey }
  | { name: "report_process_detail_opened" }
  // section defaults to "unknown" when the shortcut fires outside a report
  // section's accordion tree (e.g. a relação-graph EntityCard, which has no
  // SectionKey concept) — see useGridItemMenuConfig.ts / EntityCard.tsx.
  | { name: "report_created_from_field_shortcut"; section: SectionKey }
  // section comes from SectionTelemetryContext (ActionButton, CustomGridItemV2,
  // EntryDeleteBar each read it via useSectionTelemetryKey()), provided once
  // per section by SectionTelemetryProvider in ReportAccordionItem.tsx — no
  // prop threaded through CustomGridItem or any of the ~31 strategy files.
  // field is resolved by CustomGridItem/CustomGridItemV2 (the components that
  // actually render the field) from their wrapped child's `label`, via
  // resolveFieldKey() — "unknown" for a whole-entry delete (EntryDeleteBar,
  // and every direct ActionButton call site outside CustomGridItem) where
  // there is no single field to attribute. Fluxo 3.2/3.7.
  | { name: "report_item_deleted"; section: SectionKey; field: FieldKey; reportType: ReportTypeKey }
  // section comes from the same SectionTelemetryContext, read inside
  // CustomReadOnlyInputField itself; field is resolveFieldKey() applied to
  // that same component's own `label` prop. Fluxo 3.7, segunda metade
  // ("campo focado").
  | { name: "report_field_focused"; section: SectionKey; field: FieldKey; reportType: ReportTypeKey }
  | { name: "folder_merge"; phase: TelemetryPhase }
  // Fluxo 4.4 (notes/21367-mapeamento-fluxos-relatorios.html) — reorganização
  // de pastas via "Mover Pasta". Mesmo padrão de fases do folder_merge.
  | { name: "folder_moved"; phase: TelemetryPhase }
  // Fluxo 2.1 — busca na lista de relatórios já criados (não o formulário de
  // criação). resultCount é a contagem de itens retornados, nunca o termo
  // buscado.
  | { name: "report_list_search"; resultCount: number }
  // Fluxo 2.1, 3ª pergunta — o cliente abre o formulário de criação (1.1) e
  // submete o MESMO termo digitado numa busca da lista que acabou de retornar
  // zero resultados, dentro de uma janela curta. A comparação de termos
  // acontece só no cliente, no momento do emit
  // (searchToCreationCorrelation.helper.ts) — o termo em si nunca é
  // telemetria, só este booleano-de-fato (o evento existir É o sinal).
  | { name: "report_list_search_to_creation" };

/** Union of all valid telemetry event names, derived from the catalog. */
export type TelemetryEventName = TelemetryEvent["name"];
